import React, { useState, useEffect } from "react";
import { Toaster, toast } from "react-hot-toast";
import Select from "react-select";
import {
  createAccessPermission,
  fetchAllUserType,
  userTypeItem,
  AllMenuItem,
  fetchAllMenus,
  fetchAccessPermissionData,
} from "../Services/APIService";
import selectStyles from "../js/selectStyles.js";

interface CreateCardProps {
  onCreateComplete: () => void;
  onCancel: () => void;
}

interface FormData {
  menuID: number | "";
  subMenuId: number | "";
  userTypeId: number;
  isAccessible: boolean;
}

interface MenuSelectOption {
  value: number;
  label: string;
  menuData: AllMenuItem;
}

interface SubMenuSelectOption {
  value: number;
  label: string;
  subMenuData: { subMenuId: number; subMenuName: string };
}

const CreateAccessPermissionCard: React.FC<CreateCardProps> = ({
  onCreateComplete,
  onCancel,
}) => {
  // State for user types fetched from API
  const [userTypes, setUserTypes] = useState<userTypeItem[]>([]);
  const [menuList, setMenuList] = useState<AllMenuItem[]>([]);
  const [isLoadingUserTypes, setIsLoadingUserTypes] = useState<boolean>(true);
  const [isLoadingMenuItems, setIsLoadingMenuItems] = useState<boolean>(true);
  const [menuOptions, setMenuOptions] = useState<MenuSelectOption[]>([]);
  const [subMenuOptions, setSubMenuOptions] = useState<SubMenuSelectOption[]>(
    []
  );
  const [formData, setFormData] = useState<FormData>({
    menuID: "",
    subMenuId: "",
    userTypeId: 1, // Default value, will be updated after API fetch
    isAccessible: true,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fetch user types when component mounts
  useEffect(() => {
    const getUserTypes = async () => {
      try {
        setIsLoadingUserTypes(true);
        const types = await fetchAllUserType();

        if (types && types.length > 0) {
          setUserTypes(types);
          // Set default userTypeId to the first item in the list
          setFormData((prev) => ({
            ...prev,
            userTypeId: types[0].typeId,
          }));
        } else {
          setError("No user types found");
        }
      } catch (err) {
        setError("Failed to load user types");
        console.error("Error fetching user types:", err);
      } finally {
        setIsLoadingUserTypes(false);
      }
    };

    const getMenuItems = async () => {
      try {
        setIsLoadingMenuItems(true);

        const fetchAccessPermission = await fetchAccessPermissionData();
        const items = await fetchAllMenus();

        const allowedMenus = items.filter(
          (item) => item.subMenuList && item.subMenuList.length > 0
        );

        console.log("Check Allowed Menus:", allowedMenus);

        if (allowedMenus.length > 0) {
          setMenuList(allowedMenus);

          const firstMenu = allowedMenus[0];
          const firstSubMenuId =
            firstMenu.subMenuList?.length > 0
              ? firstMenu.subMenuList[0].subMenuId
              : "";

          setFormData((prev) => ({
            ...prev,
            menuID: firstMenu.menuID,
            subMenuId: firstSubMenuId,
          }));

          // Create options for menu react-select
          const menuOptions = allowedMenus.map((menu) => ({
            value: menu.menuID,
            label: menu.menuName,
            menuData: menu,
          }));
          setMenuOptions(menuOptions);

          // Create options for submenu react-select (for first menu)
          const subOptions =
            firstMenu.subMenuList?.map((sub) => ({
              value: sub.subMenuId,
              label: sub.subMenuName,
              subMenuData: sub,
            })) || [];
          setSubMenuOptions(subOptions);
        } else {
          setError("No menu types found");
        }
      } catch (err) {
        setError("Failed to load user types");
        console.error("Error fetching user types:", err);
      } finally {
        setIsLoadingMenuItems(false);
      }
    };

    getUserTypes();
    getMenuItems();
  }, []);

  const handleMenuSelectChange = (selectedOption: MenuSelectOption | null) => {
    if (!selectedOption) {
      // Handle clear/null case - reset to first menu item or empty
      if (menuList.length > 0) {
        const firstMenu = menuList[0];
        const defaultSubMenuId =
          firstMenu.subMenuList?.length > 0
            ? firstMenu.subMenuList[0].subMenuId
            : "";

        setFormData((prev) => ({
          ...prev,
          menuID: firstMenu.menuID,
          subMenuId: defaultSubMenuId,
        }));

        // Update submenu options for first menu
        const subOptions =
          firstMenu.subMenuList?.map((sub) => ({
            value: sub.subMenuId,
            label: sub.subMenuName,
            subMenuData: sub,
          })) || [];
        setSubMenuOptions(subOptions);
      } else {
        setFormData((prev) => ({
          ...prev,
          menuID: "",
          subMenuId: "",
        }));
        setSubMenuOptions([]);
      }
      return;
    }

    const selectedMenu = selectedOption.menuData;
    const subMenuList = selectedMenu?.subMenuList ?? [];
    const defaultSubMenuId =
      subMenuList.length > 0 ? subMenuList[0].subMenuId : "";

    setFormData((prev) => ({
      ...prev,
      menuID: selectedOption.value,
      subMenuId: defaultSubMenuId,
    }));

    // Create submenu options for the selected menu
    const subOptions = subMenuList.map((sub) => ({
      value: sub.subMenuId,
      label: sub.subMenuName,
      subMenuData: sub,
    }));
    setSubMenuOptions(subOptions);
  };

  const handleSubMenuSelectChange = (
    selectedOption: SubMenuSelectOption | null
  ) => {
    if (!selectedOption) {
      // Handle clear - set to first submenu or empty
      const currentMenu = menuList.find(
        (menu) => menu.menuID === formData.menuID
      );
      const firstSubMenu = currentMenu?.subMenuList?.[0];

      setFormData((prev) => ({
        ...prev,
        subMenuId: firstSubMenu ? firstSubMenu.subMenuId : "",
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      subMenuId: selectedOption.value,
    }));
  };

  const handleUserTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { value } = e.target;
    setFormData((prev) => ({
      ...prev,
      userTypeId: parseInt(value),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (formData.menuID === "" || isNaN(Number(formData.menuID))) {
      setError("Please enter a valid Menu ID");
      return;
    }

    console.log("formData.subMenuId:", formData.subMenuId);

    if (formData.subMenuId === "" || isNaN(Number(formData.subMenuId))) {
      setError("Please enter a valid Sub Menu ID");
      return;
    }

    try {
      setIsLoading(true);

      // Get user ID and tenant ID from localStorage
      const userId = localStorage.getItem("UserId");
      const tenantId = localStorage.getItem("tenantId");

      if (!userId || !tenantId) {
        throw new Error("User ID or Tenant ID not found in localStorage");
      }

      const fetchAccessPermission = await fetchAccessPermissionData();

      const mainMenuExists = fetchAccessPermission.some(
        (p) =>
          p.menuId === Number(formData.menuID) &&
          p.userTypeId === formData.userTypeId &&
          p.isMenu === true
      );
      const subMenuExists = fetchAccessPermission.some(
        (p) =>
          p.menuId === Number(formData.subMenuId) &&
          p.userTypeId === formData.userTypeId &&
          p.isSubMenu === true
      );

      if (!mainMenuExists) {
        const payloadMain = {
          userTypeId: formData.userTypeId,
          tenantId: parseInt(tenantId),
          menuID: Number(formData.menuID),
          isAccessible: formData.isAccessible,
          createdBy: parseInt(userId),
        };

        const resultMain = await createAccessPermission(payloadMain);

        if (!resultMain.isCreated) {
          throw new Error("Failed to create main menu permission");
        }
      }

      if (!subMenuExists) {
        const payloadSub = {
          userTypeId: formData.userTypeId,
          tenantId: parseInt(tenantId),
          menuID: Number(formData.subMenuId),
          isAccessible: formData.isAccessible,
          createdBy: parseInt(userId),
        };

        const resultSub = await createAccessPermission(payloadSub);

        if (resultSub.isCreated) {
          toast.success("Access Permission Created Successfully!");
        } else {
          throw new Error("Failed to create access permission");
        }
      } else {
        toast.error("Sub Menu Access Permission already exists!");
      }

      if (!subMenuExists) {
        // Reset form to default state
        if (menuList.length > 0) {
          const firstMenu = menuList[0];
          const firstSubMenuId =
            firstMenu.subMenuList?.length > 0
              ? firstMenu.subMenuList[0].subMenuId
              : "";

          setFormData((prev) => ({
            ...prev,
            menuID: firstMenu.menuID,
            subMenuId: firstSubMenuId,
            isAccessible: true,
          }));

          // Reset submenu options
          const subOptions =
            firstMenu.subMenuList?.map((sub) => ({
              value: sub.subMenuId,
              label: sub.subMenuName,
              subMenuData: sub,
            })) || [];
          setSubMenuOptions(subOptions);
        }

        onCreateComplete();
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "An error occurred");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    onCancel();
    setFormData({
      menuID: "",
      subMenuId: "",
      userTypeId: 1,
      isAccessible: true,
    });
    setError(null);
  };

  // Helper functions for safe value handling
  const getSelectedMenuOption = (): MenuSelectOption | null => {
    if (!formData.menuID || isNaN(Number(formData.menuID))) {
      return null;
    }

    const foundMenu = menuList.find((menu) => menu.menuID === formData.menuID);
    if (!foundMenu) {
      return null;
    }

    return {
      value: formData.menuID,
      label: foundMenu.menuName,
      menuData: foundMenu,
    };
  };

  const getSelectedSubMenuOption = (): SubMenuSelectOption | null => {
    if (!formData.subMenuId) {
      return null;
    }

    const foundOption = subMenuOptions.find(
      (sub) => sub.value === formData.subMenuId
    );

    return foundOption || null;
  };

  return (
    <div className="card mb-4">
      <div>
        <Toaster
          position="top-center"
          reverseOrder={false}
          containerStyle={{
            zIndex: 10000,
          }}
        />
      </div>
      <div className="card-header carHeaderWithBtn">
        <h5 className="card-title mb-0">Create Access Permission</h5>
      </div>
      <div className="card-body">
        {isLoadingUserTypes ? (
          <div className="d-flex justify-content-center">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading user types...</span>
            </div>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            {error && (
              <div className="alert alert-danger text-danger" role="alert">
                {error}
              </div>
            )}
            <div className="row">
              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="userType" className="form-label">
                  User Type
                </label>
                <select
                  id="userType"
                  name="userTypeId"
                  className="form-select form-control-lg"
                  value={formData.userTypeId}
                  onChange={handleUserTypeChange}
                  required
                  disabled={userTypes.length === 0}
                >
                  {userTypes.length > 0 ? (
                    userTypes.map((type) => (
                      <option key={type.typeId} value={type.typeId}>
                        {type.typeName}
                      </option>
                    ))
                  ) : (
                    <option value="">No user types available</option>
                  )}
                </select>
              </div>

              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="menuID" className="form-label">
                  Menu
                </label>
                <Select
                  options={menuOptions}
                  value={getSelectedMenuOption()}
                  onChange={handleMenuSelectChange}
                  className="basic-single-select"
                  classNamePrefix="select"
                  placeholder="Select menu"
                  isLoading={isLoadingMenuItems}
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={menuList.length === 0}
                  noOptionsMessage={() => "No menu items available"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </div>

              <div className="col-12 col-lg-6 mb-3">
                <label htmlFor="subMenuId" className="form-label">
                  Sub Menu
                </label>
                <Select
                  options={subMenuOptions}
                  value={getSelectedSubMenuOption()}
                  onChange={handleSubMenuSelectChange}
                  className="basic-single-select"
                  classNamePrefix="select"
                  placeholder="Select sub menu"
                  isLoading={isLoadingMenuItems}
                  isSearchable={true}
                  isClearable={true}
                  isDisabled={subMenuOptions.length === 0}
                  noOptionsMessage={() => "No sub menu items available"}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  styles={selectStyles}
                />
              </div>

              <div className="col-12 mt-3">
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={isLoading || userTypes.length === 0}
                >
                  {isLoading ? "Creating..." : "Submit"}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary mx-2"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
};

export default CreateAccessPermissionCard;
